import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Settings,
  Loader2,
  Clock,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsTab = ({ api, appSettings, setAppSettings, alfredSettings, fetchData }) => {
  const [savingSettings, setSavingSettings] = useState(false);

  const handleSaveAppSettings = async () => {
    setSavingSettings(true);
    try {
      const params = new URLSearchParams();
      if (appSettings.travel_buffer_minutes !== undefined) {
        params.append('travel_buffer_minutes', appSettings.travel_buffer_minutes);
      }
      if (appSettings.default_job_duration_minutes !== undefined) {
        params.append('default_job_duration_minutes', appSettings.default_job_duration_minutes);
      }
      
      const response = await api().patch(`/admin/settings?${params.toString()}`);
      setAppSettings(response.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUpdateAlfredSettings = async (field, value) => {
    try {
      await api().patch(`/alfred/settings?${field}=${encodeURIComponent(value)}`);
      toast.success('Settings updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  return (
    <div className="space-y-6">
      {/* Scheduling Settings */}
      <Card className="border-zinc-100">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Scheduling Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="travel-buffer">Travel Buffer (minutes)</Label>
              <p className="text-sm text-zinc-500 mb-2">Time added between jobs for travel</p>
              <Input
                id="travel-buffer"
                type="number"
                value={appSettings?.travel_buffer_minutes || 30}
                onChange={(e) => setAppSettings({ ...appSettings, travel_buffer_minutes: parseInt(e.target.value) || 30 })}
                className="max-w-[150px]"
              />
            </div>
            <div>
              <Label htmlFor="default-duration">Default Job Duration (minutes)</Label>
              <p className="text-sm text-zinc-500 mb-2">Default time allocated for new jobs</p>
              <Input
                id="default-duration"
                type="number"
                value={appSettings?.default_job_duration_minutes || 120}
                onChange={(e) => setAppSettings({ ...appSettings, default_job_duration_minutes: parseInt(e.target.value) || 120 })}
                className="max-w-[150px]"
              />
            </div>
          </div>
          <Button onClick={handleSaveAppSettings} disabled={savingSettings}>
            {savingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Settings
          </Button>
        </CardContent>
      </Card>

      {/* Alfred/Integration Settings */}
      {alfredSettings && (
        <Card className="border-zinc-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Integration Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Alfred Integration</Label>
                <p className="text-sm text-zinc-500">Enable external service integration</p>
              </div>
              <Switch
                checked={alfredSettings.enabled}
                onCheckedChange={(checked) => handleUpdateAlfredSettings('enabled', checked)}
              />
            </div>
            {alfredSettings.enabled && (
              <>
                <div className="flex items-center justify-between py-2 border-t border-zinc-100">
                  <div>
                    <Label>Auto-sync</Label>
                    <p className="text-sm text-zinc-500">Automatically sync data with external services</p>
                  </div>
                  <Switch
                    checked={alfredSettings.auto_sync}
                    onCheckedChange={(checked) => handleUpdateAlfredSettings('auto_sync', checked)}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* System Info */}
      <Card className="border-zinc-100">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-5 w-5" />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div>
              <p className="text-zinc-500">Application</p>
              <p className="font-medium">T Touch Cleaning Services</p>
            </div>
            <div>
              <p className="text-zinc-500">Version</p>
              <p className="font-medium">2.0.0</p>
            </div>
            <div>
              <p className="text-zinc-500">Environment</p>
              <p className="font-medium">Production</p>
            </div>
            <div>
              <p className="text-zinc-500">Timezone</p>
              <p className="font-medium">Australia/Sydney (AEST)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsTab;
